import React, { useEffect, useState } from "react";
import SlDialog from "@shoelace-style/shoelace/dist/react/dialog";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import Button from "../reusable/Button/Button";
import ButtonOutline from "../reusable/ButtonOutline/ButtonOutline";
import ProgressBar from "../reusable/ProgressBar/ProgressBar";
import { EstadoTablasMirrorResponse } from "../../interfaces/dashboardTypes";
import { SyncSnapshot } from "../../hooks/useSyncProgress";
import "./ModalSyncUpsertHash.css";

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (tabla: string) => Promise<void> | void;
  tablas: EstadoTablasMirrorResponse;
  tablasSoportadas: string[];
  snapshots?: Record<string, SyncSnapshot>;
  timeoutMs?: number;
}

/**
 * Modal para sincronización de tablas mirror mediante flujo upsert-hash.
 * Muestra la lista de tablas del registry que soportan este flujo y pide
 * confirmación por tabla antes de disparar la sincronización.
 */
function ModalSyncUpsertHash({
  show,
  onClose,
  onConfirm,
  tablas,
  tablasSoportadas,
  snapshots = {},
  timeoutMs,
}: Props) {
  const [tablaAConfirmar, setTablaAConfirmar] = useState<string>("");

  useEffect(() => {
    if (!show) {
      setTablaAConfirmar("");
    }
  }, [show]);

  const tablasFiltradas = tablas.filter((t) =>
    tablasSoportadas.includes(t.clave)
  );

  const handleSolicitarConfirmacion = (clave: string) => {
    setTablaAConfirmar(clave);
  };

  const handleCancelarConfirmacion = () => {
    setTablaAConfirmar("");
  };

  const handleConfirmar = async (clave: string) => {
    await onConfirm(clave);
    setTablaAConfirmar("");
  };

  return (
    <SlDialog
      label="Sincronización Upsert Hash"
      open={show}
      onSlAfterHide={onClose}
      className="shoelace-modal modal-sync-upsert-hash"
      // @ts-ignore
      style={{ "--width": "640px" }}
    >
      <div className="upsert-hash-container">
        <div className="upsert-hash-info">
          <SlIcon name="info-circle" className="upsert-hash-info-icon" />
          <div>
            <p className="upsert-hash-info-title">
              ¿Qué hace esta sincronización?
            </p>
            <p className="upsert-hash-info-text">
              A diferencia del flujo clásico, este proceso{" "}
              <strong>no elimina todos los registros</strong> antes de insertar.
              Usa un hash por registro para identificar los cambios y aplicar
              solo los <strong>nuevos</strong>, los{" "}
              <strong>modificados</strong> y los <strong>eliminados</strong>.
            </p>
          </div>
        </div>

        <div className="upsert-hash-tablas">
          <p className="upsert-hash-tablas-title">
            Tablas disponibles en el registry upsert-hash
          </p>

          {tablasFiltradas.length === 0 ? (
            <p className="upsert-hash-empty">
              No hay tablas configuradas para el flujo upsert-hash.
            </p>
          ) : (
            <ul className="upsert-hash-list">
              {tablasFiltradas.map((t) => {
                const enConfirmacion = tablaAConfirmar === t.clave;
                const desactivada = t.estado === "DESACTIVADA";
                const snapshot = snapshots[t.clave];
                const enProceso =
                  !!snapshot &&
                  (snapshot.status === "preparing" ||
                    snapshot.status === "polling");

                return (
                  <li key={t.clave} className="upsert-hash-item">
                    <div className="upsert-hash-item-content">
                      <div className="upsert-hash-item-info">
                        <div className="upsert-hash-item-clave">
                          <span
                            className={desactivada ? "text-red-500" : ""}
                            style={{ fontWeight: 600 }}
                          >
                            {t.clave}
                          </span>
                          {desactivada && (
                            <span className="upsert-hash-dot" />
                          )}
                        </div>
                        <div className="upsert-hash-item-meta">
                          <span>
                            Oracle:{" "}
                            <strong>{t.cantidadRegistrosOracle ?? "-"}</strong>
                          </span>
                          <span>
                            Mirror:{" "}
                            <strong>{t.cantidadRegistros ?? "-"}</strong>
                          </span>
                          <span>
                            Últ. sinc.:{" "}
                            <strong>{t.ultimaSincronizacion || "-"}</strong>
                          </span>
                        </div>
                      </div>

                      <div className="upsert-hash-item-actions">
                        {enProceso || snapshot ? null : enConfirmacion ? (
                          <div className="upsert-hash-confirm">
                            <span className="upsert-hash-confirm-text">
                              ¿Confirmar sincronización?
                            </span>
                            <div className="upsert-hash-confirm-buttons">
                              <ButtonOutline
                                label="Cancelar"
                                variant="warning"
                                onClick={handleCancelarConfirmacion}
                              />
                              <Button
                                label="Sí, sincronizar"
                                theme="yellow"
                                onClick={() => handleConfirmar(t.clave)}
                              />
                            </div>
                          </div>
                        ) : (
                          <Button
                            label="Sincronizar"
                            iconName="arrow-repeat"
                            size="small"
                            theme="primary"
                            onClick={() =>
                              handleSolicitarConfirmacion(t.clave)
                            }
                          />
                        )}
                      </div>
                    </div>

                    {snapshot && (
                      <div className="upsert-hash-progress-wrapper">
                        <ProgressBar
                          percent={snapshot.percent}
                          status={snapshot.status}
                          elapsedMs={snapshot.elapsedMs}
                          timeoutMs={timeoutMs}
                          cantidadRegistros={snapshot.cantidadRegistros}
                          cantidadRegistrosOracle={
                            snapshot.cantidadRegistrosOracle
                          }
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="upsert-hash-footer">
          <ButtonOutline
            label="Cerrar"
            variant="neutral"
            onClick={onClose}
          />
        </div>
      </div>
    </SlDialog>
  );
}

export default ModalSyncUpsertHash;
